/* Modulo: Header Pill
   Scroll state (stuck / fixed / scroll-up), the traveling nav capsule,
   the search + mobile-menu panels, a page-level cart watcher, and the
   optional cart drawer. Sections don't get the theme extension's core
   runtime, so this rolls its own init. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var SELECTOR = '[data-modulo-hpi]';
  var STUCK_AT = 24;   // px scrolled before the bar turns solid
  var HIDE_AT  = 260;  // px before scroll-up may hide the bar
  var SLOP     = 4;    // ignore sub-pixel scroll jitter

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  /* Markets: never hardcode a path. window.Shopify.routes.root is the only
     value documented as always ending in '/'. On a Nordic subfolder market
     it is '/en-se/', and a literal '/cart.js' reads the wrong market. */
  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ═══════════════════════════════════════════════════════════════
     The scroll lock  (merchant report, 2026-09-05)
     ═══════════════════════════════════════════════════════════════
     `html.modulo-hpi-locked { overflow: hidden }` stops the page scrolling
     behind an open drawer. It must live on <html> — the ONE node a soft
     navigation or an in-place header re-render never rebuilds — while the code
     that removed it used to be a per-init closure that either of those
     destroys. Measured: replace the header markup while the drawer is open (a
     page transition, or a theme/app re-rendering the header for the cart count,
     which our own steppers provoke) and the class outlives its only remover.
     The page is dead, nothing explains it, and only a reload clears it.

     So the lock is not OWNED, it is DERIVED from the one thing the DOM always
     answers: is a live drawer open? Nothing is remembered, so nothing can be
     orphaned, and two headers refcount for free.
     Pinned by scripts/probe-header-pill-scroll-lock.mjs. */
  var LOCK = 'modulo-hpi-locked';
  var lockWatch = null;

  /* Only a drawer whose instance is still alive counts: a re-render clones the
     markup verbatim, `is-open` included, so the document can hold one that LOOKS
     open with no listeners behind it. Close that orphan rather than honour it —
     an undismissable drawer on an unscrollable page is the worst outcome here. */
  function liveOpenDrawer() {
    var open = document.querySelectorAll('[data-hpi-drawer].is-open');
    for (var i = 0; i < open.length; i++) {
      var host = open[i].closest ? open[i].closest('[data-modulo-hpi]') : null;
      if (host && host.moduloHpi) return open[i];
      open[i].classList.remove('is-open');
      var wrap = host && host.closest ? host.closest('.modulo-header-pill-section') : null;
      if (wrap) wrap.classList.remove('is-cart-open');
    }
    return null;
  }

  function syncLock() {
    /* The observer can outlive the document that armed it. Let go, don't throw. */
    if (typeof document === 'undefined' || !document.documentElement) {
      if (lockWatch) { lockWatch.disconnect(); lockWatch = null; }
      return;
    }
    var open = liveOpenDrawer();
    document.documentElement.classList.toggle(LOCK, !!open);

    /* Only while locked, and only childList: an attribute change cannot re-enter
       this and an unlocked page pays nothing. Catches what the boot reconcile
       cannot — the header replaced while this script does NOT re-execute, since
       an inserted <script src> never runs. This scope outlives that DOM. */
    if (open && !lockWatch && window.MutationObserver) {
      lockWatch = new MutationObserver(syncLock);
      lockWatch.observe(document.documentElement, { childList: true, subtree: true });
    } else if (!open && lockWatch) {
      lockWatch.disconnect();
      lockWatch = null;
    }
  }

  /* ═══════════════════════════════════════════════════════════════
     The cart watcher (one per page, shared by every header instance)
     ═══════════════════════════════════════════════════════════════
     The count is server-rendered, so it is right on every page LOAD and
     wrong from the first AJAX add onwards. Themes keep their OWN bubble
     current — Dawn re-renders the `cart-icon-bubble` section through the
     Section Rendering API and publishes on an internal PubSub — and none
     of that reaches a section we ship. So the numeral sat at its
     render-time value until the next full document load. Our own blocks
     (upsell, bundle, sticky ATC, Quick Buy) have the same blind spot:
     they ask for the THEME's cart sections, which is a different header.

     There is no cross-theme cart event to subscribe to, so the mutation
     itself is what gets observed: any /cart/add|change|update|clear that
     settles — on fetch OR XHR, ours or the theme's or another app's —
     schedules one coalesced /cart.js read that is pushed to every
     subscriber.

     Deliberately NOT gated on a 2xx: Shopify can apply a PARTIAL quantity
     and still answer 422, so "the request failed" does not mean "the cart
     is unchanged". */
  var MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|#|$)/i;

  /* Themes and apps that announce their own cart writes. Belt and braces
     behind the taps: a theme that captured window.fetch before we did is
     invisible to the wrapper but usually still dispatches one of these.
     `cart:refresh` / `cart:updated` / `cart:update` are what our own
     extension blocks emit. */
  var CART_EVENTS = [
    'cart:refresh', 'cart:updated', 'cart:update', 'cart-update',
    'cart:change', 'cart:added', 'cart:build', 'product:added'
  ];

  function cartHub() {
    if (window.moduloHpiCart) return window.moduloHpiCart;

    var hub = {
      data: null,      // last known /cart.js payload
      subs: [],
      pending: null,   // debounce timer
      inflight: null,
      dirty: false,    // a mutation landed while a read was in flight
      lastAt: 0
    };
    window.moduloHpiCart = hub;

    hub.push = function (data) {
      if (!data || typeof data.item_count !== 'number') return data;
      hub.data = data;
      hub.lastAt = Date.now();
      for (var i = 0; i < hub.subs.length; i++) {
        try { hub.subs[i](data); } catch (e) { /* one bad subscriber must not stop the rest */ }
      }
      return data;
    };

    hub.subscribe = function (fn) {
      hub.subs.push(fn);
      if (hub.data) { try { fn(hub.data); } catch (e) {} }
      return function () {
        var i = hub.subs.indexOf(fn);
        if (i > -1) hub.subs.splice(i, 1);
      };
    };

    hub.read = function () {
      if (hub.inflight) {
        /* Coalescing a concurrent caller onto the in-flight read would hand
           it a payload taken BEFORE the mutation that prompted this call —
           so remember to go again rather than answering with stale data. */
        hub.dirty = true;
        return hub.inflight;
      }
      if (!window.fetch) return Promise.resolve(null);

      hub.inflight = window.fetch(shopRoot() + 'cart.js', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          hub.inflight = null;
          hub.push(data);
          if (hub.dirty) { hub.dirty = false; hub.read(); }
          return data;
        })
        .catch(function () {
          hub.inflight = null;
          hub.dirty = false;
          return null; // a stale count is not worth a console error
        });

      return hub.inflight;
    };

    hub.schedule = function () {
      if (hub.pending) clearTimeout(hub.pending);
      /* Trailing debounce: a quantity stepper held down, or Dawn's
         add-then-render pair, is one read rather than one per request. */
      hub.pending = setTimeout(function () {
        hub.pending = null;
        hub.read();
      }, 60);
    };

    function urlOf(input) {
      if (typeof input === 'string') return input;
      if (input && typeof input.url === 'string') return input.url;  // Request object
      try { return String(input); } catch (e) { return ''; }
    }

    /* ── tap: fetch ────────────────────────────────────────────────
       The caller gets the ORIGINAL promise back, so the theme's own
       .then/.catch chain, its error handling and its response body are
       all untouched — we only watch it settle. Both arms are handled so
       observing a theme rejection can never turn it into an unhandled
       one. */
    if (window.fetch && !window.fetch.moduloHpiTap) {
      var nativeFetch = window.fetch;
      var tapped = function () {
        var url = urlOf(arguments[0]);
        var p = nativeFetch.apply(window, arguments);
        if (MUTATION.test(url) && p && typeof p.then === 'function') {
          p.then(function () { hub.schedule(); }, function () { hub.schedule(); });
        }
        return p;
      };
      tapped.moduloHpiTap = true;
      window.fetch = tapped;
    }

    /* ── tap: XMLHttpRequest ───────────────────────────────────────
       jQuery-era themes (Debut, Brooklyn and their forks) and a fair
       number of apps still add to cart over XHR, which the fetch wrapper
       cannot see. */
    var XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype && !XHR.prototype.moduloHpiTap) {
      var nativeOpen = XHR.prototype.open;
      var nativeSend = XHR.prototype.send;

      XHR.prototype.open = function (method, url) {
        try { this.moduloHpiUrl = url; } catch (e) {}
        return nativeOpen.apply(this, arguments);
      };
      XHR.prototype.send = function () {
        var self = this;
        try {
          if (MUTATION.test(String(self.moduloHpiUrl || ''))) {
            // loadend, not load: an aborted or failed request may still have
            // reached the cart, same reason the status is not checked.
            self.addEventListener('loadend', function () { hub.schedule(); });
          }
        } catch (e) {}
        return nativeSend.apply(this, arguments);
      };
      XHR.prototype.moduloHpiTap = true;
    }

    for (var i = 0; i < CART_EVENTS.length; i++) {
      document.addEventListener(CART_EVENTS[i], function () { hub.schedule(); });
    }

    /* bfcache restores the DOM verbatim, the lock and an open drawer included,
       so reconcile the two against each other rather than trusting either. */
    window.addEventListener('pageshow', function (e) {
      syncLock();
      if (e.persisted) hub.read();
    });

    /* A cart changed in another tab. Floored at 10s so tab-hopping cannot
       turn into one request per focus change on a busy store. */
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - hub.lastAt < 10000) return;
      hub.read();
    });

    return hub;
  }

  /* ═══════════════════════════════════════════════════════════════
     Money
     ═══════════════════════════════════════════════════════════════
     /cart.js returns integer subunits and no formatted string, and the
     theme's own formatMoney is not safe to borrow (Ella's throws when
     called with one argument). So the shop's money_format comes in on a
     data attribute and is rendered here.

     When the cart is presenting a currency other than the shop's own
     (Shopify Markets), that format carries the WRONG symbol — the amounts
     from /cart.js are already converted. Intl is the correct answer
     there, and only there: on the primary market the merchant's own
     configured format wins. */
  function formatWith(cents, format) {
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = String(format || '').match(placeholder);
    var name = match ? match[1] : 'amount';

    function num(precision, thousands, decimal) {
      var value = (Math.abs(cents) / 100).toFixed(precision);
      var parts = value.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var rest = parts[1] ? decimal + parts[1] : '';
      return (cents < 0 ? '-' : '') + whole + rest;
    }

    var value;
    switch (name) {
      case 'amount_no_decimals':                     value = num(0, ',', '.'); break;
      case 'amount_with_comma_separator':            value = num(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = num(0, '.', ','); break;
      case 'amount_with_apostrophe_separator':       value = num(2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = num(0, ' ', ','); break;
      case 'amount_with_space_separator':            value = num(2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = num(2, ' ', '.'); break;
      default:                                       value = num(2, ',', '.');
    }
    return match ? format.replace(placeholder, value) : value;
  }

  /* Two formats, because Shopify's own price-display guidance splits them:
     cart and checkout TOTALS must always carry the currency code, itemized
     cart prices must not (the surrounding context already implies it, and
     the short form fits the row). money_format / money_with_currency_format
     are the merchant's own configured strings for exactly those two jobs. */
  function makeMoney(format, shopCurrency) {
    return function (cents, cartCurrency) {
      if (typeof cents !== 'number') return '';
      if (cartCurrency && shopCurrency && cartCurrency !== shopCurrency &&
          window.Intl && window.Intl.NumberFormat) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: cartCurrency
          }).format(cents / 100);
        } catch (e) { /* unknown code — fall through to the shop format */ }
      }
      return formatWith(cents, format);
    };
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;   // never innerHTML: this is merchant + shopper data
    return node;
  }

  /* Shopify's image CDN takes a width query param, and item.image already
     carries a ?v= cache key. */
  function sized(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function init(root) {
    if (root.moduloHpi) return;

    /* A freshly rendered header never ships an open drawer, so one found here
       came from a re-render that copied the markup: stale by definition, and
       adopting it would keep the page locked behind a drawer nobody can close. */
    var stale = root.querySelector('[data-hpi-drawer].is-open');
    if (stale) {
      stale.classList.remove('is-open');
      var staleWrap = root.closest ? root.closest('.modulo-header-pill-section') : null;
      if (staleWrap) staleWrap.classList.remove('is-cart-open');
    }
    syncLock();

    var behavior = root.dataset.hpiBehavior || 'sticky';
    var list     = root.querySelector('[data-hpi-list]');
    var glide    = root.querySelector('[data-hpi-glide]');
    var scrim    = root.querySelector('[data-hpi-scrim]');
    var cartLink = root.querySelector('[data-hpi-cart]');
    var count    = root.querySelector('[data-hpi-count]');

    var panels = {};
    var toggles = root.querySelectorAll('[data-hpi-toggle]');
    Array.prototype.forEach.call(root.querySelectorAll('[data-hpi-panel]'), function (p) {
      panels[p.dataset.hpiPanel] = p;
    });

    var state = {
      open: null,
      lastY: window.pageYOffset || 0,
      ticking: false,
      trigger: null,
      cleanup: []
    };
    root.moduloHpi = state;

    function on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      state.cleanup.push(function () { target.removeEventListener(type, fn, opts); });
    }

    /* ── the traveling capsule ───────────────────────────────
       Anchored to the list's physical left edge and moved by a
       physical delta, so it tracks under RTL as well. */
    function moveGlide(link) {
      if (!glide || !list) return;
      var r = link.getBoundingClientRect();
      var b = list.getBoundingClientRect();
      glide.style.setProperty('--modulo-hpi-gx', (r.left - b.left - 12) + 'px');
      glide.style.setProperty('--modulo-hpi-gw', (r.width + 24) + 'px');
    }
    if (list && glide) {
      Array.prototype.forEach.call(list.querySelectorAll('.modulo-hpi__link'), function (a) {
        on(a, 'mouseenter', function () { moveGlide(a); });
        on(a, 'focus', function () { moveGlide(a); });
      });
    }

    /* ── panels ──────────────────────────────────────────────*/
    function setPanel(name) {
      var next = name && panels[name] ? name : null;

      Object.keys(panels).forEach(function (key) {
        panels[key].classList.toggle('is-open', key === next);
      });
      Array.prototype.forEach.call(toggles, function (btn) {
        btn.setAttribute('aria-expanded', String(btn.dataset.hpiToggle === next));
      });
      root.classList.toggle('is-panel', !!next);

      if (next) {
        var focusable = panels[next].querySelector('input, a, button');
        if (focusable) {
          // Wait for visibility to flip — a hidden element can't take focus.
          window.setTimeout(function () { focusable.focus(); }, 80);
        }
      } else if (state.trigger) {
        state.trigger.focus();
        state.trigger = null;
      }
      state.open = next;
    }

    Array.prototype.forEach.call(toggles, function (btn) {
      on(btn, 'click', function () {
        var name = btn.dataset.hpiToggle;
        if (state.open === name) {
          setPanel(null);
        } else {
          state.trigger = btn;
          setPanel(name);
        }
      });
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-hpi-close]'), function (btn) {
      on(btn, 'click', function () { setPanel(null); });
    });
    if (scrim) on(scrim, 'click', function () { setPanel(null); });

    /* ── scroll state ────────────────────────────────────────*/
    var head = root.querySelector('.modulo-hpi__head');
    var bar  = root.querySelector('.modulo-hpi__bar');
    var inEditor = !!(window.Shopify && window.Shopify.designMode);

    /* Everything that keeps `position: fixed` actually fixed on a real theme
       lives in the shared engine — see modulo-section-header-pin.js, which the
       commerce head uses too. The no-op fallback keeps the header working if
       that asset ever fails to load: it degrades to a plain sticky header
       rather than throwing on the first scroll. */
    var pin = window.moduloHeaderPin
      ? window.moduloHeaderPin(root, {
          head: head, bar: bar, wrap: '.modulo-header-pill-section', cleanup: state.cleanup
        })
      : {
          scrollY: function () { return window.pageYOffset || document.documentElement.scrollTop || 0; },
          findScroller: function () { return null; },
          ensureVisible: function () {},
          onStick: function () {}
        };

    function onScroll() {
      var y = pin.scrollY();
      var stuck = y > STUCK_AT;

      root.classList.toggle('is-stuck', stuck);

      if (behavior === 'static') {
        state.lastY = y;
        return;
      }

      if (stuck) pin.onStick(root.classList.contains('is-hidden'));
      root.classList.toggle('is-fixed', stuck);

      if (behavior === 'scroll_up' && !inEditor) {
        // Never hide the bar while a panel is open, or the search field
        // the shopper is typing into slides off screen.
        if (y > state.lastY + SLOP && y > HIDE_AT && !state.open) {
          root.classList.add('is-hidden');
        } else if (y < state.lastY - SLOP || y <= HIDE_AT) {
          root.classList.remove('is-hidden');
        }
      }
      state.lastY = y;
      pin.ensureVisible();
    }

    var sc = pin.findScroller();
    if (sc) on(sc, 'scroll', function () {
      if (state.ticking) return;
      state.ticking = true;
      window.requestAnimationFrame(function () {
        // finally, or one throw inside onScroll leaves ticking latched true and
        // the header never updates again for the life of the page.
        try { onScroll(); } finally { state.ticking = false; }
      });
    }, { passive: true });

    on(window, 'scroll', function () {
      if (state.ticking) return;
      state.ticking = true;
      window.requestAnimationFrame(function () {
        // finally, or one throw inside onScroll leaves ticking latched true and
        // the header never updates again for the life of the page.
        try { onScroll(); } finally { state.ticking = false; }
      });
    }, { passive: true });

    /* ═══ cart ═══════════════════════════════════════════════ */
    var hub = (cartLink || count) ? cartHub() : null;

    var drawer   = root.querySelector('[data-hpi-drawer]');
    var panel    = drawer && drawer.querySelector('[data-hpi-drawer-panel]');
    var body     = drawer && drawer.querySelector('[data-hpi-drawer-body]');
    var foot     = drawer && drawer.querySelector('[data-hpi-drawer-foot]');
    var totalEl  = drawer && drawer.querySelector('[data-hpi-total]');
    var live     = drawer && drawer.querySelector('[data-hpi-live]');
    var closeBtn = drawer && drawer.querySelector('[data-hpi-drawer-close-btn]');
    var wrapper  = root.closest ? root.closest('.modulo-header-pill-section') : null;

    var shopCurrency = (drawer && drawer.dataset.hpiShopCurrency) || '';
    var money = makeMoney((drawer && drawer.dataset.hpiMoney) || '${{amount}}', shopCurrency);
    var moneyTotal = makeMoney(
      (drawer && drawer.dataset.hpiMoneyTotal) || (drawer && drawer.dataset.hpiMoney) || '${{amount}}',
      shopCurrency
    );

    var iconTpl = {};
    if (drawer) {
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-hpi-icon]'), function (t) {
        iconTpl[t.dataset.hpiIcon] = t;
      });
    }
    function icon(name) {
      var tpl = iconTpl[name];
      if (!tpl || !tpl.content) return document.createTextNode('');
      return tpl.content.cloneNode(true);
    }

    var cartOpen = false;
    var lastFocus = null;
    var busy = false;

    function label(key, fallback) {
      return (drawer && drawer.dataset[key]) || fallback;
    }

    /* ── the badge ────────────────────────────────────────────*/
    function paintCount(data) {
      var n = data && typeof data.item_count === 'number' ? data.item_count : 0;
      if (count) {
        count.textContent = String(n);
        if (n > 0) count.removeAttribute('hidden');
        else count.setAttribute('hidden', '');
      }
      if (cartLink) {
        var name = cartLink.dataset.hpiCartLabel || 'Cart';
        cartLink.setAttribute('aria-label', name + ' (' + n + ')');
      }
    }

    /* ── the drawer body ──────────────────────────────────────*/
    function renderLine(item, currency) {
      var li = el('li', 'modulo-hpi__line');
      /* Addressed by line item KEY, never by index. Shopify's own guidance:
         a cart can hold several lines with the same variant_id (different
         properties, or automatic discounts splitting it across prices), and
         an index is only true for as long as the list it was read from. The
         cart can change between paint and click — another tab, a bundle app,
         a subscription swap — and then a 1-based index quietly edits the
         WRONG product. The key cannot drift. */
      li.setAttribute('data-hpi-key', String(item.key || ''));

      var photo = (item.featured_image && item.featured_image.url) || item.image || '';
      var media = el(item.url ? 'a' : 'div', 'modulo-hpi__line-media');
      if (item.url) media.setAttribute('href', item.url);
      media.setAttribute('aria-hidden', 'true');
      media.setAttribute('tabindex', '-1');   // the title beside it is the real link
      if (photo) {
        var img = el('img');
        img.setAttribute('src', sized(photo, 160));
        img.setAttribute('srcset', sized(photo, 160) + ' 1x, ' + sized(photo, 320) + ' 2x');
        img.setAttribute('alt', '');
        img.setAttribute('width', '80');
        img.setAttribute('height', '80');
        img.setAttribute('loading', 'lazy');
        media.appendChild(img);
      } else {
        media.classList.add('modulo-hpi__line-media--empty');
      }
      li.appendChild(media);

      var main = el('div', 'modulo-hpi__line-main');

      var title = el(item.url ? 'a' : 'span', 'modulo-hpi__line-title', item.product_title || item.title || '');
      if (item.url) title.setAttribute('href', item.url);
      main.appendChild(title);

      var meta = [];
      if (item.variant_title && item.variant_title !== 'Default Title') meta.push(item.variant_title);
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        meta.push(item.selling_plan_allocation.selling_plan.name);
      }
      if (item.properties) {
        for (var key in item.properties) {
          if (!Object.prototype.hasOwnProperty.call(item.properties, key)) continue;
          if (key.charAt(0) === '_' || !item.properties[key]) continue;   // _ prefixed are private
          meta.push(key + ': ' + item.properties[key]);
        }
      }
      if (meta.length) main.appendChild(el('span', 'modulo-hpi__line-meta', meta.join(' · ')));

      var discounts = item.line_level_discount_allocations || [];
      for (var d = 0; d < discounts.length; d++) {
        var app = discounts[d].discount_application || {};
        main.appendChild(el('span', 'modulo-hpi__line-disc',
          (app.title || 'Discount') + ' (−' + money(discounts[d].amount, currency) + ')'));
      }

      /* Quantity. Each control names its own product — a row of bare
         "+" buttons is a row of identically-named controls. */
      var qty = el('div', 'modulo-hpi__qty');
      var name = item.product_title || item.title || '';

      var minus = el('button', 'modulo-hpi__qty-btn');
      minus.type = 'button';
      minus.setAttribute('data-hpi-qty', '-1');
      minus.setAttribute('aria-label', label('hpiTDecrease', 'Decrease quantity of') + ' ' + name);
      minus.appendChild(icon('minus'));

      var plus = el('button', 'modulo-hpi__qty-btn');
      plus.type = 'button';
      plus.setAttribute('data-hpi-qty', '1');
      plus.setAttribute('aria-label', label('hpiTIncrease', 'Increase quantity of') + ' ' + name);
      plus.appendChild(icon('plus'));

      qty.appendChild(minus);
      qty.appendChild(el('span', 'modulo-hpi__qty-n', String(item.quantity)));
      qty.appendChild(plus);
      main.appendChild(qty);
      li.appendChild(main);

      var side = el('div', 'modulo-hpi__line-side');
      if (typeof item.original_line_price === 'number' &&
          item.original_line_price !== item.final_line_price) {
        side.appendChild(el('s', 'modulo-hpi__line-was', money(item.original_line_price, currency)));
      }
      side.appendChild(el('span', 'modulo-hpi__line-price',
        money(typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price, currency)));

      var remove = el('button', 'modulo-hpi__line-x', label('hpiTRemove', 'Remove'));
      remove.type = 'button';
      remove.setAttribute('data-hpi-remove', '');
      remove.setAttribute('aria-label', label('hpiTRemove', 'Remove') + ' ' + name);
      side.appendChild(remove);
      li.appendChild(side);

      return li;
    }

    /* The list is rebuilt wholesale on every cart change, which destroys the
       control the shopper is standing on. Without this, a keyboard user
       stepping a quantity up loses focus to <body> on every single press.
       Matched by iteration rather than a selector: a line item key contains
       a ':' and would need escaping to be queried. */
    function stashFocus() {
      var active = document.activeElement;
      if (!active || !body.contains(active) || !active.closest) return null;
      var row = active.closest('[data-hpi-key]');
      if (!row) return null;
      var role = null;
      if (active.hasAttribute('data-hpi-qty')) role = 'qty:' + active.getAttribute('data-hpi-qty');
      else if (active.hasAttribute('data-hpi-remove')) role = 'remove';
      if (!role) return null;
      return { key: row.getAttribute('data-hpi-key'), role: role };
    }

    function restoreFocus(stash) {
      if (!stash) return;
      var rows = body.querySelectorAll('[data-hpi-key]');
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].getAttribute('data-hpi-key') !== stash.key) continue;
        var sel = stash.role === 'remove'
          ? '[data-hpi-remove]'
          : '[data-hpi-qty="' + stash.role.split(':')[1] + '"]';
        var btn = rows[i].querySelector(sel);
        if (btn) { btn.focus(); return; }
      }
      /* The line is gone — removed, or stepped down to zero. Focus has to
         land somewhere inside the dialog or it falls out of the trap. */
      if (closeBtn) closeBtn.focus();
    }

    function render(data) {
      if (!drawer || !body) return;
      var items = (data && data.items) || [];
      var currency = data && data.currency;
      var stash = stashFocus();

      body.replaceChildren();

      if (!items.length) {
        var empty = el('div', 'modulo-hpi__empty');
        empty.appendChild(el('p', 'modulo-hpi__empty-t', label('hpiTEmpty', 'Your cart is empty')));
        var shop = el('a', 'modulo-hpi__empty-a', label('hpiTContinue', 'Continue shopping'));
        shop.setAttribute('href', drawer.dataset.hpiHome || '/');
        empty.appendChild(shop);
        body.appendChild(empty);
        if (foot) foot.setAttribute('hidden', '');
        if (stash && closeBtn) closeBtn.focus();
        return;
      }

      var ul = el('ul', 'modulo-hpi__lines');
      for (var i = 0; i < items.length; i++) ul.appendChild(renderLine(items[i], currency));
      body.appendChild(ul);

      if (foot) foot.removeAttribute('hidden');
      if (totalEl) totalEl.textContent = moneyTotal(data.total_price, currency);
      restoreFocus(stash);
    }

    function setBusy(on) {
      if (drawer) drawer.classList.toggle('is-busy', !!on);
    }

    /* ── open / close ─────────────────────────────────────────*/
    function openCart(e) {
      if (!drawer || !hub) return;                 // page mode: let the link navigate
      if (e) e.preventDefault();
      if (cartOpen) return;

      setPanel(null);                              // never two overlays at once
      cartOpen = true;
      lastFocus = document.activeElement;
      if (wrapper) wrapper.classList.add('is-cart-open');
      drawer.classList.add('is-open');
      syncLock();
      if (cartLink) cartLink.setAttribute('aria-expanded', 'true');

      if (hub.data) render(hub.data);
      else setBusy(true);

      // Focus lands after the visibility flip, same reason as the panels.
      window.setTimeout(function () { if (cartOpen && closeBtn) closeBtn.focus(); }, 60);

      hub.read().then(function (data) {
        setBusy(false);
        /* Nothing to show and nothing cached — the cart page is the honest
           fallback rather than an empty drawer that may be a lie. */
        if (!data && !hub.data && cartLink && cartLink.getAttribute('href')) {
          /* Close FIRST: leaving with the lock applied hands the shopper a
             locked document if they come Back through bfcache. */
          closeCart();
          window.location.assign(cartLink.getAttribute('href'));
        }
      });
    }

    function closeCart() {
      if (!drawer || !cartOpen) return;
      cartOpen = false;
      drawer.classList.remove('is-open');
      if (wrapper) wrapper.classList.remove('is-cart-open');
      syncLock();
      if (cartLink) {
        cartLink.setAttribute('aria-expanded', 'false');
        if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
        else cartLink.focus();
      }
      lastFocus = null;
    }

    if (cartLink && drawer) {
      on(cartLink, 'click', openCart);
      /* Warm the payload before the click so the first open paints from
         cache. One request, only on intent, and only once. */
      var warmed = false;
      var warm = function () {
        if (warmed || !hub) return;
        warmed = true;
        if (!hub.data) hub.read();
      };
      on(cartLink, 'pointerenter', warm);
      on(cartLink, 'focus', warm);
    }

    if (drawer) {
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-hpi-drawer-close]'), function (btn) {
        on(btn, 'click', function (e) { e.preventDefault(); closeCart(); });
      });

      /* Line controls are delegated: render() replaces the whole list on
         every cart change, so per-node listeners would die with it. */
      on(drawer, 'click', function (e) {
        var target = e.target;
        if (!target || !target.closest) return;

        var step = target.closest('[data-hpi-qty]');
        var kill = step ? null : target.closest('[data-hpi-remove]');
        if (!step && !kill) return;

        var row = (step || kill).closest('[data-hpi-key]');
        if (!row) return;
        var key = row.getAttribute('data-hpi-key');
        if (!key) return;

        if (kill) return change(key, 0);

        var current = parseInt((row.querySelector('.modulo-hpi__qty-n') || {}).textContent, 10) || 0;
        var next = current + parseInt(step.getAttribute('data-hpi-qty'), 10);
        change(key, next < 0 ? 0 : next);
      });

      on(drawer, 'keydown', function (e) {
        if (e.key !== 'Tab' || !cartOpen || !panel) return;
        var nodes = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (n) {
          return n.offsetWidth || n.offsetHeight || n.getClientRects().length;
        });
        if (!nodes.length) return;
        var first = nodes[0];
        var last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }

    /* One serialised writer. Shopify answers HTTP 409 to simultaneous
       cart requests, and a stepper is exactly the control a shopper
       double-taps. */
    function change(key, quantity) {
      if (busy || !window.fetch) return;
      busy = true;
      setBusy(true);

      window.fetch(shopRoot() + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        // `id` as the line item key — a string, per the Ajax API contract.
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data) return;
          hub.push(data);   // one path: the badge, the lines and the total all repaint from here
          if (live) {
            live.textContent = label('hpiTUpdated', 'Cart updated') + ' — ' +
              data.item_count + ' ' + (data.item_count === 1
                ? label('hpiTItem', 'item')
                : label('hpiTItems', 'items'));
          }
        })
        .catch(function () { /* the watcher's own read will reconcile */ })
        .then(function () {
          busy = false;
          setBusy(false);
        });
    }

    on(document, 'keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (cartOpen) closeCart();
      else if (state.open) setPanel(null);
    });

    // A click anywhere outside the header closes an open panel.
    on(document, 'click', function (e) {
      if (state.open && !root.contains(e.target)) setPanel(null);
    });

    if (hub) {
      var off = hub.subscribe(function (data) {
        paintCount(data);
        if (cartOpen || (drawer && drawer.classList.contains('is-open'))) render(data);
      });
      state.cleanup.push(off);
      state.cleanup.push(closeCart);   // an unloaded section must not leave the page scroll-locked
    }

    onScroll();
    pin.ensureVisible();
    // A hero image landing late can change what paints over the bar.
    on(window, 'load', pin.ensureVisible);
  }

  function destroy(root) {
    var state = root.moduloHpi;
    if (!state) return;
    state.cleanup.forEach(function (off) { try { off(); } catch (e) {} });
    root.moduloHpi = null;
  }

  function bootstrap(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(host.querySelectorAll(SELECTOR), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootstrap(document); });
  } else {
    bootstrap(document);
  }

  // Theme editor: re-init on section load, tear listeners down on unload
  // so repeated edits don't stack duplicate window handlers.
  //
  // Re-inserting the section re-inserts this <script src>, and an
  // identical src still EXECUTES again — so the document-level
  // listeners have to be wired exactly once per page, not per run.
  if (!window.moduloHpiWired) {
    window.moduloHpiWired = true;

    document.addEventListener('shopify:section:load', function (e) {
      bootstrap(e.target || document);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var target = e.target;
      if (!target || !target.querySelectorAll) return;
      Array.prototype.forEach.call(target.querySelectorAll(SELECTOR), destroy);
    });
  }
})();
